export interface DeliveryWindow {
  name: string;
  value: string;
}

const DEFAULT_START_H = 7;
const DEFAULT_START_M = 0;
const DEFAULT_END_H = 22;
const DEFAULT_END_M = 0;

function parseToMinutes(time: string): number {
  const [h, m] = time.split(":");
  const hours = Number(h);
  const minutes = m === undefined ? 0 : Number(m);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return NaN;
  return hours * 60 + minutes;
}

// Never offer a slot before the maker can actually fulfil it.
function snapUpToGrid(minutes: number): number {
  return Math.ceil(minutes / 30) * 30;
}

// Never offer a slot that ends after the maker is actually closed.
function snapDownToGrid(minutes: number): number {
  return Math.floor(minutes / 30) * 30;
}

const WINDOW_DURATION_MINUTES = 30;
// Adjacent 15-min-stepped starts intentionally produce overlapping 30-min
// windows (e.g. 09:00-09:30 and 09:15-09:45 both offered) — finer start
// granularity for a delivery ETA, not a capacity/resource slot. Not a bug.
const SLOT_STEP_MINUTES = 15;

/**
 * Generate delivery windows between a maker's open/close time. Each
 * returned entry is the START of a 30-minute window (TimePicker.vue always
 * renders `name` to `name + 30min`), but consecutive starts are offered
 * every 15 minutes (09:00, 09:15, 09:30…) so the customer can pick a finer
 * start point while every option remains a full 30-minute window.
 * `open_time`/`close_time` are free-text DB fields that aren't guaranteed to
 * fall on a :00/:30 boundary (e.g. "09:15") — the open bound is snapped UP
 * and the close bound snapped DOWN to the nearest half-hour (never the
 * numerically nearest boundary in either direction), so a maker is never
 * shown as available before they're actually open or after they're closed.
 * The last offered start is capped so its 30-minute window never extends
 * past the snapped close bound.
 *
 * The 07:00–22:00 bounds are defaults used when open_time/close_time is missing
 * or fails to parse. open_time is also floored at 07:00, while close_time is
 * otherwise honored as-is (snapped down to the grid), never clamped earlier.
 * (22:00 matches the backend's validated close_time ceiling — see makersController's
 * ExpectedTime and the DeliveryDay model default).
 */
export function generateDeliveryWindows(
  openTime?: string,
  closeTime?: string,
): DeliveryWindow[] {
  let startMinutes = DEFAULT_START_H * 60 + DEFAULT_START_M;
  let endMinutes = DEFAULT_END_H * 60 + DEFAULT_END_M;

  if (openTime) {
    const rawStart = snapUpToGrid(parseToMinutes(openTime));
    if (Number.isFinite(rawStart)) {
      startMinutes = Math.max(rawStart, startMinutes);
    }
  }
  if (closeTime) {
    const rawEnd = snapDownToGrid(parseToMinutes(closeTime));
    if (Number.isFinite(rawEnd)) {
      endMinutes = rawEnd;
    }
  }

  const lastValidStart = endMinutes - WINDOW_DURATION_MINUTES;

  const windows: DeliveryWindow[] = [];
  for (let t = startMinutes; t <= lastValidStart; t += SLOT_STEP_MINUTES) {
    const h = String(Math.floor(t / 60)).padStart(2, "0");
    const m = String(t % 60).padStart(2, "0");
    const name = `${h}:${m}`;
    windows.push({ name, value: name });
  }

  return windows;
}
